import { getDateString } from '../../../../utils/constants';
import en from '../../../../lang/en-us';
import { useHistory, useLocation } from 'react-router';
import { CANCEL, SipSummaryObjectType, SipTransactionPayloadType, PAUSE } from './types';
import { maskAccountNo } from '../../../../utils/utilityFunctions';
import SipTransactionDetails from './SipTransactionDetails';
import { formatAsIndianCurrency } from '../../../../utils/currency';
import { useRef, useState } from 'react';
import SIPButton, { SIPCancelDialog, SipReasonsLoadingDialog } from './SipButton';
import { sipValidate } from './utils/validate';
import { useDispatch, useSelector } from 'react-redux';
import { TRXN_TYPE_MAP } from '../utils';
import { PanSeedCheckNew } from '../../../common/PanSeedCheckNew';
import { SIPCancelReason } from './SIPCancelReason';
import { RootStateType } from '../../../../redux-store/reducers';

const { dbCommercial } = en;
const CollapseData = ({ row }: { row: SipSummaryObjectType }) => {
    const values: { label: string; value: string }[] = [
        {
            label: 'Fund House',
            value: row.amcName,
        },
        {
            label: 'Folio',
            value: row.folio,
        },
        {
            label: 'Scheme',
            value: row.schemeName,
        },
        {
            label: 'Sip Date',
            value: row.sipDate,
        },
        {
            label: 'Frequency',
            value: row.frequency,
        },
        {
            label: 'Transaction No',
            value: !!row.userTrxnNo ? row.userTrxnNo : 'NA',
        },
        {
            label: 'Installment Amount',
            value: formatAsIndianCurrency(+row.amount),
        },
        {
            label: 'Start Date',
            value: getDateString(row.startDate),
        },
        {
            label: 'End Date',
            value: getDateString(row.endDate),
        },
        {
            label: 'Bank Name',
            value: row.bank?.name,
        },
        {
            label: 'Account No.',
            value: maskAccountNo(row.bank?.accountNo) || '',
        },
        {
            label: 'Ifsc',
            value: row.bank?.ifsc,
        },
        {
            label: 'No of Installments',
            value: row.noOfInstallments,
        },
    ];
    return (
        <>
            {values.map(({ label, value }, index) => (
                <div key={index} className="individual-element">
                    <h5 className="title">{label}</h5>
                    <p>{value ? value : 'N/A'}</p>
                </div>
            ))}
        </>
    );
};

const shouldShowSipButtons = (status: string) => {
    const validStatuses = ['active', 'live sip'];
    return validStatuses.includes(status.toLowerCase());
};

const SipPauseDetails = () => {
    const {
        state: { data: row },
    } = useLocation<{ data: SipSummaryObjectType }>();
    const history = useHistory();
    const dispatch = useDispatch();
    const { loading } = useSelector(({ sipCancelReducer }: RootStateType) => ({
        ...sipCancelReducer
    }))

    const [buttonClicked, setButtonClicked] = useState<'PSIP' | 'CSIP' | null>(null);
    const [apiCalled, setApiCalled] = useState(false);
    const [cancelPayload, setCancelPayload] = useState(null);
    const [remarks, setRemarks] = useState('');
    const [reqId, setReqId] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [showReasonDialog, setShowReasonDialog] = useState(false);

    const transactionPayload: SipTransactionPayloadType = {
        folio: row.folio,
        schemeCode: row.schemeCode,
        txnType: 'SIP',
        userTrxnNo: row.userTrxnNo,
    };

    const callApi = (trxnType: 'PSIP' | 'CSIP') => async () => {
        const sipValidated = sipValidate(row, dispatch, history, setCancelPayload, setShowReasonDialog, setRemarks, setReqId);
        try {
            setShowDialog(false);
            setApiCalled(true);
            setButtonClicked(trxnType);
            await sipValidated(trxnType);
        } catch (err: any) {
            alert(err.message);
        } finally {
            setButtonClicked(null);
            setApiCalled(false);
        }
    };
    const callPauseApi = callApi('PSIP');
    const callCancelApi = callApi('CSIP');

    const handleCallBackFn = (args: any[] | undefined) => {
        if (args) {
            const text = args[0].target.textContent;
            if (text === PAUSE) {
                callPauseApi();
            } else {
                setShowDialog(true);
            }
        }
    };

    const checkPanSeedRef = useRef<((...args: any[]) => void) | null>(null);

    const handleButtonClick = (e: any) => {
        if (checkPanSeedRef.current) {
            checkPanSeedRef.current(event);
        }
    };

    return (
        <div className="curved-bg ct-track">
            <PanSeedCheckNew
                customPayload={{
                    amc: row.amc,
                    taxStatus: '',
                    folio: row.folio,
                    trxnMode: 'FT',
                    trxnType: TRXN_TYPE_MAP.sipPause,
                }}
                triggerCheck={false}
                callBackFn={handleCallBackFn}
                checkPanSeedRef={checkPanSeedRef}
            />
            <h2 className="heading">
                <a onClick={() => history.goBack()}>
                    <img src="/images/left-arrow-black.svg" alt="" />
                </a>
                {dbCommercial.ctLinks.sipPauseDetails}
            </h2>
            <div className="curved-bg-content curved-bg-table-outer">
                <div className="ct-track-content">
                    <div className="ct-track-content-inner" style={{ position: 'relative' }}>
                        <CollapseData row={row} />
                        {shouldShowSipButtons(row.sipStatus) && (
                            <>
                                <SIPButton
                                    text={PAUSE}
                                    right={100}
                                    loading={buttonClicked === 'PSIP'}
                                    handleSubmit={handleButtonClick}
                                    apiCalled={apiCalled}
                                />
                                <SIPButton
                                    text={CANCEL}
                                    loading={buttonClicked === 'CSIP'}
                                    apiCalled={apiCalled}
                                    handleSubmit={handleButtonClick}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
            <SipTransactionDetails payload={transactionPayload} amcCode={row.amc} />
            <SipReasonsLoadingDialog loading={loading} />
            <SIPCancelDialog
                showDialog={showDialog}
                setShowReasonDialog={setShowReasonDialog}
                setShowDialog={setShowDialog}
                callPauseApi={callPauseApi}
                callCancelApi={callCancelApi}
                disabled={apiCalled}
                setButtonClicked={setButtonClicked}
            />
            <SIPCancelReason
                setShowReasonDialog={setShowReasonDialog}
                sipCancelPayload={cancelPayload}
                trxnType={buttonClicked!}
                reqId={reqId}
                remarks={remarks}
                showDialog={showReasonDialog}
                callPauseApi={callPauseApi}
                callCancelApi={callCancelApi}
                disabled={apiCalled}
                setButtonClicked={setButtonClicked}
            />
        </div>
    );
};

export default SipPauseDetails;
